import { BlocksSnippetAri } from '@atlassian/ari';

import { cfetch } from '@confluence/network';
import { getSessionData } from '@confluence/session-data/entry-points/SessionData.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';

import {
	type Snippet,
	SnippetBodyFormat,
	type SnippetOwner,
	SnippetProduct,
	type SnippetStatus,
} from '../snippetTypes.ts';
import { parseSnippetContent } from '../snippetUtils.ts';

const COMMON_HEADERS = {
	'Content-Type': 'application/json',
	Accept: 'application/json',
};

const BLOCK_SERVICE_API_URL = '/gateway/api/blocks/v1';
const SNIPPETS_PATH = '/snippets';

export class SnippetError extends Error {
	constructor(readonly status: number) {
		super(`Snippet error: ${status}`);
	}
}

export type SnippetData = {
	resourceId: string; // ARI of the Snippet
	snippetId: string;
	name: string; // User defined name
	description?: string; // User defined name
	body: string; // String of the type defined in `bodyFormat` (example: "ADF")
	bodyFormat: SnippetBodyFormat;
	product: SnippetProduct;
	label?: string;
	isFavorite: 0 | 1;
	status: SnippetStatus;
	createdBy: string;
	createdAt: string; // String of integer timestamp
	updatedAt: string; // String of integer timestamp
};

export type CreateSnippetBody = {
	name: string;
	body: string;
	bodyFormat: SnippetBodyFormat;
	product: SnippetProduct;
	description?: string;
};

const transformSnippets = async (snippets: SnippetData[]): Promise<Snippet[]> => {
	const { displayName } = await getSessionData();
	return snippets
		.map((snippet: SnippetData) => transformSnippetData(snippet, displayName))
		.filter((snippet: Snippet | undefined) => snippet !== undefined);
};

const transformSnippetData = (
	snippet: SnippetData,
	displayName: string | null,
): Snippet | undefined => {
	const parsedBody = parseSnippetContent(snippet.body);
	if (parsedBody === undefined) {
		return undefined;
	}

	const createdBy: SnippetOwner = {
		accountId: snippet.createdBy,
		displayName:
			snippet.createdBy?.toLowerCase() === 'atlassian'
				? 'Atlassian'
				: displayName || snippet.createdBy,
	};

	return {
		ari: BlocksSnippetAri.parse(snippet.resourceId),
		snippetId: snippet.snippetId,
		name: snippet.name,
		description: snippet.description,
		bodyFormat: snippet.bodyFormat,
		body: parsedBody,
		product: snippet.product,
		isFavorite: Boolean(snippet.isFavorite),
		status: snippet.status,
		createdBy,
		createdAt: new Date(Number(snippet.createdAt)),
		updatedAt: new Date(Number(snippet.updatedAt)),
	};
};

type FetchSnippetScope = 'user' | 'global';

let fetchSnippetsPromise: Promise<Snippet[]> | null = null;
let cachedSnippets: Snippet[] | null = null;

/**
 * Fetch snippets from the Block Service for Confluence. https://bitbucket.org/atlassian/block-service/src/main/
 * Please note that no error handling is done. Ensure you are handling bad response codes when using this.
 *
 * This function caches both the promise (for concurrent requests) and the result (for subsequent calls)
 * to prevent duplicate HTTP requests.
 */
export const fetchSnippets = async (): Promise<Snippet[]> => {
	if (fetchSnippetsPromise) {
		return fetchSnippetsPromise;
	}

	if (cachedSnippets !== null) {
		return cachedSnippets;
	}

	fetchSnippetsPromise = (async () => {
		try {
			const [userSnippetsResult, globalSnippetsResult] = await Promise.allSettled([
				fetchSnippetData('user'),
				fetchSnippetData('global'),
			]);

			const successfulSnippets: SnippetData[] = [];

			if (userSnippetsResult.status === 'fulfilled') {
				successfulSnippets.push(...userSnippetsResult.value);
			} else {
				logResultError(userSnippetsResult.reason, 'user');
			}

			if (globalSnippetsResult.status === 'fulfilled') {
				successfulSnippets.push(...globalSnippetsResult.value);
			} else {
				logResultError(globalSnippetsResult.reason, 'global');
			}

			const result = await transformSnippets(successfulSnippets);
			cachedSnippets = result;

			return result;
		} finally {
			// Clear the promise cache after the promise resolves/rejects
			// but keep cachedSnippets so subsequent calls return cached data
			fetchSnippetsPromise = null;
		}
	})();

	return fetchSnippetsPromise;
};

/**
 * Clear the snippets cache. This will force the next call to fetchSnippets() to make new HTTP requests.
 */
export const clearSnippetsCache = (): void => {
	cachedSnippets = null;
	fetchSnippetsPromise = null;
};

const fetchSnippetData = async (scope: FetchSnippetScope): Promise<SnippetData[]> => {
	const params = new URLSearchParams({
		format: SnippetBodyFormat.ADF,
		product: SnippetProduct.CONFLUENCE,
		scope,
	});

	const response = await cfetch(`${BLOCK_SERVICE_API_URL + SNIPPETS_PATH}?${params.toString()}`, {
		method: 'GET',
		headers: COMMON_HEADERS,
	});

	if (!response.ok) {
		throw new SnippetError(response.status);
	}

	const responseData = await response.json();
	if (!responseData.success) {
		return [];
	}

	// Success and error can both be present, so we need to log the error if it is present
	if (Array.isArray(responseData.error) && responseData.error.length > 0) {
		responseData.error.forEach((error: any) => {
			const errorToLog = new Error(
				`Error when fetching ${scope} scoped snippets: ${error?.message || JSON.stringify(error)}`,
			);
			getMonitoringClient().submitError(errorToLog, {
				attribution: Attribution.INTEGRATIONS,
			});
		});
	}

	return responseData.success;
};

export const createSnippet = async (params: CreateSnippetBody): Promise<Snippet> => {
	const response = await cfetch(`${BLOCK_SERVICE_API_URL + SNIPPETS_PATH}`, {
		method: 'POST',
		headers: COMMON_HEADERS,
		body: JSON.stringify(params),
	});

	if (!response.ok) {
		throw new SnippetError(response.status);
	}

	const responseData: SnippetData = await response.json();
	const { displayName } = await getSessionData();
	const snippet = transformSnippetData(responseData, displayName);

	// Clear the cache so the newly created snippet appears when fetching snippets again
	// (e.g., when navigating to a different page or when the extension provider rebuilds)
	// addSnippetQuickInsertItem only adds it to current editor instance's quick insert menu
	// navigating to a new edit page the extension providers rebuilds and call fetchSnippets which will return the cached data.
	clearSnippetsCache();

	if (snippet) {
		return snippet;
	} else {
		throw new Error('Unable to transform snippet data after creation');
	}
};

const logResultError = (reason: unknown, scope: FetchSnippetScope): void => {
	const reasonString = reason instanceof Error ? reason.message : JSON.stringify(reason);

	const errorToLog = new Error(`Error when fetching ${scope} scoped snippets: ${reasonString}`);
	getMonitoringClient().submitError(errorToLog, {
		attribution: Attribution.INTEGRATIONS,
	});
};
